import { InteractionRequiredAuthError } from '@azure/msal-browser'

import { apiConfig, msalInstance } from '../../authConfig'
import {
  CreateQcTestDefinitionPayload,
  QcTestDefinition,
  QcTestDefinitionListItem,
  QcTestExecutionDetail,
  QcTestExecutionHistoryPage,
  QcTestExecutionSubmission,
  ScoreParameterDto,
  ScoreParameterType,
} from '../../types'
import apiClient from '../apiClient'
import { ENDPOINTS } from '../endpoints'
import { qualityTestService } from './qualityTest.service'

// Note: The API returns camelCase (likely due to axios or backend configuration)
// even though .NET typically uses PascalCase. These interfaces match the actual response.
export const fetchQualityTests = async (siteId: string): Promise<QcTestDefinitionListItem[]> => {
  const response = await apiClient.get<QcTestDefinitionListItem[]>(ENDPOINTS.qualityTests.list(siteId))
  return response.data
}

export const fetchQualityTestById = async (siteId: string, testId: string): Promise<QcTestDefinition> => {
  const response = await apiClient.get<Omit<QcTestDefinition, 'form'> & { form: string }>(
    `/api/quality-tests/${siteId}/${testId}`,
  )
  // Parse the form JSON string
  return {
    ...response.data,
    form: JSON.parse(response.data.form),
  }
}

// POST /api/quality-test-executions/{versionId}
export const submitTestExecution = async (
  testVersionId: string,
  submission: QcTestExecutionSubmission,
): Promise<void> => {
  await apiClient.post(`/api/quality-test-executions/${testVersionId}`, submission)
}

export const fetchTestHistory = async (
  siteId: string,
  testId: string,
  page: number,
  pageSize: number,
): Promise<QcTestExecutionHistoryPage> => {
  return qualityTestService.getHistory(siteId, testId, {
    pageNumber: page,
    pageSize,
  })
}

export const fetchTestExecutionById = async (siteId: string, executionId: string): Promise<QcTestExecutionDetail> => {
  const response = await apiClient.get<Omit<QcTestExecutionDetail, 'formJson'> & { formJson: string }>(
    ENDPOINTS.qualityTestExecutions.get(siteId, executionId),
  )
  return {
    ...response.data,
    formJson: JSON.parse(response.data.formJson) as Record<string, unknown>,
  }
}

export const softDeleteTestExecution = async (executionId: string): Promise<void> => {
  await apiClient.delete(`/api/quality-test-executions/${executionId}`)
}

export const restoreTestExecution = async (executionId: string): Promise<void> => {
  await apiClient.post(`/api/quality-test-executions/${executionId}/restore`)
}

export const uploadSurveyAttachment = async (file: File, siteId: string): Promise<string> => {
  const formData = new FormData()
  formData.append('file', file)
  // Use fetch instead of Axios: Axios's instance-level Content-Type: application/json default
  // interferes with FormData uploads (either JSON-serialises the body or strips the header
  // entirely, causing the server to receive no Content-Type). fetch with a FormData body
  // always sets multipart/form-data; boundary=... correctly.
  // Note: This uses fetch directly (not apiClient), so we must handle token acquisition manually.
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0]
  if (!account) throw new Error('No active account')

  const request = {
    scopes: apiConfig.scopes,
    account: account,
  }

  const tokenResponse = await msalInstance.acquireTokenSilent(request).catch(async (error) => {
    if (error instanceof InteractionRequiredAuthError) {
      return await msalInstance.acquireTokenPopup(request)
    }
    throw error
  })
  if (!tokenResponse) throw new Error('Failed to acquire token')
  const response = await fetch(`${apiConfig.webApiUri}${ENDPOINTS.qualityControl.attachments.upload(siteId)}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${tokenResponse.accessToken}` },
    body: formData,
  })
  if (!response.ok) throw new Error(`Upload failed: ${response.status}`)
  const data = (await response.json()) as { url: string }
  return data.url
}

export const deleteSurveyAttachment = async (url: string, siteId: string): Promise<void> => {
  await apiClient.delete(ENDPOINTS.qualityControl.attachments.delete(siteId), {
    data: { url },
  })
}

export const downloadSurveyAttachment = async (url: string, siteId: string): Promise<string> => {
  const response = await apiClient.get<Blob>(ENDPOINTS.qualityControl.attachments.proxy(siteId), {
    params: { blobUrl: url },
    responseType: 'blob',
  })

  return URL.createObjectURL(response.data)
}

// --- Admin types ---

export type { ScoreParameterType, ScoreParameterDto }

export interface AdminUpdateQcTestRequest {
  displayName: string
  category?: string
  description?: string
  displayOrder?: number
  form: string
  canTestMultipleEntities: boolean
  scoreCalculatorType: string
  scoreParameters: ScoreParameterDto[]
  scheduledActivationDate?: string // ISO UTC string
  targetTestsPerShift?: number
}

export interface AdminUpdateQcTestResponse {
  id: string
  testGroupId: string
  displayName: string
  version: number
  displayOrder?: number
  scheduledActivationDate?: string
}

export interface QcTestVersionSummary {
  id: string
  testGroupId: string
  version: number
  displayName: string
  category?: string
  isCurrentlyActive: boolean
  createdAt: string
  modifiedAt: string
  scheduledActivationDate: string // ISO UTC string, always set
  hasExecutions: boolean
  targetTestsPerShift?: number
}

// --- Admin service functions ---

export const fetchTestParameters = (testDefinitionId: string, siteId: string, testId?: string): Promise<ScoreParameterDto[]> =>
  apiClient
    .get(`/api/quality-tests/${testDefinitionId}/parameters`, {
      params: { siteId, ...(testId ? { testId } : {}) },
    })
    .then((r) => r.data)

export const fetchVersionHistory = (testDefinitionId: string, siteId: string): Promise<QcTestVersionSummary[]> =>
  apiClient.get(`/api/quality-tests/${testDefinitionId}/versions`, { params: { siteId } }).then((r) => r.data)

export const adminUpdateTestDefinition = (
  testDefinitionId: string,
  siteId: string,
  payload: AdminUpdateQcTestRequest,
): Promise<AdminUpdateQcTestResponse> =>
  apiClient.put(`/api/quality-tests/${testDefinitionId}`, payload, { params: { siteId } }).then((r) => r.data)

export const fetchScoreCalculatorTypes = (siteId: string): Promise<string[]> =>
  apiClient.get('/api/quality-tests/score-calculator-types', { params: { siteId } }).then((r) => r.data)

export const recomputeAllScores = async (siteId: string): Promise<{ recomputedCount: number }> => {
  const response = await apiClient.post<{ recomputedCount: number }>('/api/quality-tests/recompute-scores', null, { params: { siteId } })
  return response.data
}

export interface AdminCreateQcTestResponse {
  id: string
  testGroupId: string
  displayName: string
  version: number
}

export const adminCreateTestDefinition = (siteId: string, payload: CreateQcTestDefinitionPayload): Promise<AdminCreateQcTestResponse> =>
  apiClient.post('/api/quality-tests', payload, { params: { siteId } }).then((r) => r.data)
